import { useMemo, useState, type CSSProperties, type ReactElement } from "react";
import { ArrowLeft, CheckCheck, MessageSquare, Plus } from "lucide-react";
import { color, space } from "@tokens/index";
import { Button, ContentSwap, IconButton, SkeletonRows, Text, useViewport } from "../../primitives";
import { classifyTaskStatus } from "../taskStatus";
import type { WorkItem } from "../../../experience-shell/workflows";
import { ComposerFooter, MessageComposer, MessageStream } from "./Messaging";
import { StartThreadComposer } from "./StartThreadComposer";
import { ThreadAnswerStatus, ThreadList } from "./Threads";
import { EmptyState } from "./shared";
import { hairline } from "./expandMotion";
import { sceneMeasures } from "./measures";
import { staleDataStyle } from "../staleData";
import type { SelectedThreadInfo, ThreadSectionProps } from "./types";

/** The active-thread body that both the skeleton and the message stream fill. */
const paneColumn: CSSProperties = { flex: 1, display: "flex", flexDirection: "column", minHeight: 0 };

/**
 * Child threads of a task or note. On `expanded` and up the thread list
 * sits beside the active thread. On `medium` and `compact` the two stack:
 * the list or the active thread, with a back control in the pane header.
 */
export function ThreadSection({
  threads,
  isLoadingThreads,
  isThreadsStale,
  selectedThreadId,
  onSelectThread,
  activeThread,
  isLoadingActiveThread,
  isActiveThreadStale,
  now,
  currentUserId,
  resolveQueueName,
  queueTree,
  originatingQueueId,
  approverQueueIds,
  isSending,
  isStartingThread,
  isApprovingThreadAnswerId,
  isRejectingThreadAnswerId,
  onSendThreadMessage,
  onStartThread,
  onApproveThreadAnswer,
  onRejectThreadAnswer,
  onCloseThread,
  isClosingThread,
}: ThreadSectionProps): ReactElement {
  const [startingNew, setStartingNew] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const { isAtLeast, isCompact } = useViewport();
  const sideBySide = isAtLeast("expanded");

  const threadMessages = useMemo(
    () => (activeThread?.workItems ?? []).filter((i) => i.type === "message"),
    [activeThread],
  );
  const hasThreads = threads.length > 0;

  // The seed task of the active thread carries the asker's approval contract.
  const threadAnswerSeed = useMemo<WorkItem | null>(() => {
    if (activeThread === undefined) return null;
    return activeThread.workItems.find((i) => i.type === "task" && i.requestedByQueueId !== null) ?? null;
  }, [activeThread]);

  const canApproveThreadAnswer =
    threadAnswerSeed !== null &&
    threadAnswerSeed.requestedByQueueId !== null &&
    approverQueueIds?.has(threadAnswerSeed.requestedByQueueId) === true;

  const selectedThreadInfo = useMemo<SelectedThreadInfo | null>(() => {
    if (activeThread === undefined || threadAnswerSeed === null) return null;
    const status = classifyTaskStatus(threadAnswerSeed, now);
    const recipientIds = threadAnswerSeed.visibleToQueueIds.filter((id) => id !== threadAnswerSeed.requestedByQueueId);
    let queueName: string | null = null;
    for (const id of recipientIds) {
      const name = resolveQueueName(id);
      if (name !== undefined) {
        queueName = name;
        break;
      }
    }
    const lastMsg = threadMessages.at(-1);
    return { status, queueName, preview: lastMsg?.body ?? null };
  }, [activeThread, threadAnswerSeed, now, resolveQueueName, threadMessages]);

  const showThreadList = threads.length > 1;
  const stacked = showThreadList && !sideBySide;
  const showListOnly = stacked && listOpen;
  const activeTitle = threads.find((t) => t.id === selectedThreadId)?.title ?? activeThread?.title;

  const showActiveThread = !isLoadingThreads && hasThreads && !startingNew;
  const canClose = showActiveThread && onCloseThread !== undefined && threadMessages.length > 0;
  const threadClosed = canClose && !threadMessages.some((m) => m.completedAt === null);
  // One header row: back (when stacked), title, and the close control. The
  // message stream below it is the scroller, so the header keeps its place
  // without sticky positioning.
  const showPaneHeader = stacked || canClose;

  const paneHeader = showPaneHeader ? (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: space[2],
        padding: `${space[1]} ${space[2]}`,
        borderBottom: hairline,
        background: color.bg.surface,
        flexShrink: 0,
        minWidth: 0,
      }}
    >
      {stacked && (
        <IconButton icon={ArrowLeft} variant="ghost" size="md" aria-label="Back to threads" onClick={() => setListOpen(true)} />
      )}
      <Text role="body" size="sm" weight="medium" truncate style={{ flex: 1, minWidth: 0 }}>
        {startingNew ? "New thread" : (activeTitle ?? "Thread")}
      </Text>
      {canClose &&
        (threadClosed ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: space[1], color: color.status.successFg, flexShrink: 0 }}>
            <CheckCheck size={13} strokeWidth={2.5} aria-hidden="true" />
            <Text role="caption" weight="medium" style={{ color: "inherit" }}>
              Thread closed
            </Text>
          </span>
        ) : (
          <Button
            variant="ghost"
            size={isCompact ? "sm" : "md"}
            disabled={isClosingThread}
            busy={isClosingThread}
            onClick={onCloseThread}
            style={{ flexShrink: 0 }}
          >
            <CheckCheck size={14} strokeWidth={2.5} aria-hidden="true" />
            Close thread
          </Button>
        ))}
    </div>
  ) : null;

  const list = showThreadList ? (
    <ThreadList
      threads={threads}
      isLoading={isLoadingThreads}
      isStale={isThreadsStale}
      selectedThreadId={selectedThreadId}
      onSelectThread={(id) => {
        onSelectThread(id);
        setStartingNew(false);
        setListOpen(false);
      }}
      selectedThreadInfo={selectedThreadInfo}
      onStartNew={() => {
        setStartingNew(true);
        setListOpen(false);
      }}
      bordered={sideBySide}
    />
  ) : null;

  const pane = (
    <div style={{ display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0, flex: 1 }}>
      {paneHeader}
      {isLoadingThreads ? (
        <div style={{ flex: 1, padding: space[3] }}>
          <SkeletonRows label="Loading threads" rows={3} />
        </div>
      ) : !hasThreads || startingNew ? (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <EmptyState
            icon={<MessageSquare size={24} strokeWidth={1.5} aria-hidden="true" />}
            title={startingNew ? "Start another thread." : "No threads yet."}
          />
          <ComposerFooter>
            <div style={{ display: "flex", flexDirection: "column", gap: space[2] }}>
              <StartThreadComposer
                queueTree={queueTree}
                originatingQueueId={originatingQueueId}
                disabled={isStartingThread}
                onStart={(draft) => {
                  onStartThread(draft);
                  setStartingNew(false);
                }}
              />
              {startingNew && (
                <Button variant="ghost" size="sm" onClick={() => setStartingNew(false)} style={{ alignSelf: "flex-start" }}>
                  Cancel
                </Button>
              )}
            </div>
          </ComposerFooter>
        </div>
      ) : (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          {threadAnswerSeed !== null && (
            <ThreadAnswerStatus
              seed={threadAnswerSeed}
              now={now}
              canApprove={canApproveThreadAnswer}
              isApproving={isApprovingThreadAnswerId === threadAnswerSeed.id}
              isRejecting={isRejectingThreadAnswerId === threadAnswerSeed.id}
              onApprove={onApproveThreadAnswer}
              onReject={onRejectThreadAnswer}
            />
          )}
          <ContentSwap
            // `selectedThreadId === null` with threads on hand is the gap before
            // the shell selects the first thread. A skeleton there is stable;
            // the empty hint would flash.
            ready={!(activeThread === undefined && (isLoadingActiveThread || selectedThreadId === null))}
            style={paneColumn}
            layerStyle={paneColumn}
            skeleton={
              <div style={{ flex: 1, padding: space[3] }}>
                <SkeletonRows label="Loading thread" rows={3} />
              </div>
            }
          >
            <div style={{ ...paneColumn, ...staleDataStyle(isActiveThreadStale) }}>
              <MessageStream
                items={threadMessages}
                currentUserId={currentUserId}
                resolveQueueName={resolveQueueName}
                emptyHint="No messages in this thread yet."
              />
            </div>
          </ContentSwap>
          <ComposerFooter>
            <div style={{ display: "flex", flexDirection: "column", gap: space[2] }}>
              <MessageComposer placeholder="Reply in this thread…" disabled={isSending} onSend={onSendThreadMessage} />
              {!showThreadList && (
                <Button variant="ghost" size="sm" onClick={() => setStartingNew(true)} style={{ alignSelf: "flex-start" }}>
                  <Plus size={14} strokeWidth={2.5} aria-hidden="true" />
                  Start another thread
                </Button>
              )}
            </div>
          </ComposerFooter>
        </div>
      )}
    </div>
  );

  if (showListOnly) {
    return <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0 }}>{list}</div>;
  }

  return (
    <div
      style={{
        flex: 1,
        display: "grid",
        gridTemplateColumns:
          showThreadList && sideBySide ? `${String(sceneMeasures.threadListWidth)}px minmax(0, 1fr)` : "minmax(0, 1fr)",
        minHeight: 0,
        minWidth: 0,
      }}
    >
      {showThreadList && sideBySide && list}
      {pane}
    </div>
  );
}
